/**
 * camelCase ↔ snake_case mappers for all Supabase tables.
 * Shared by syncService (hydration) and all cloud-first hooks.
 */
import type {
  Team, Season, Player, SeasonRoster, Game, StatEntry,
  Lineup, LineupSlot, CalendarEvent, Opponent, DrillTemplate,
} from '@/types'

export function makeTeamCode(): string {
  return Math.random().toString(36).slice(2, 8).toUpperCase()
}

// ── Teams ────────────────────────────────────────────────────────────────────

export function teamToRow(t: Team, userId: string) {
  return {
    id: t.id, user_id: userId,
    team_code: t.teamCode || makeTeamCode(),
    name: t.name,
    sport_profile_id: t.sportProfileId,
    school: t.school ?? null, age_group: t.ageGroup ?? null, league: t.league ?? null,
    logo_url: t.logoUrl ?? null, colors: t.colors ?? null,
    period_config: t.periodConfig ?? null, stat_overrides: t.statOverrides ?? null,
    roster_card_fields: t.rosterCardFields ?? null,
    created_at: t.createdAt, updated_at: t.updatedAt,
  }
}
export function rowToTeam(r: Record<string, unknown>): Team {
  return {
    id: r.id as string,
    teamCode: (r.team_code as string | undefined) ?? (r.id as string).replace(/-/g, '').slice(0, 6).toUpperCase(),
    name: r.name as string, sportProfileId: r.sport_profile_id as string,
    school: r.school as string | undefined, ageGroup: r.age_group as string | undefined,
    league: r.league as string | undefined, logoUrl: r.logo_url as string | undefined,
    colors: r.colors as Team['colors'], periodConfig: r.period_config as Team['periodConfig'],
    statOverrides: r.stat_overrides as Team['statOverrides'],
    rosterCardFields: r.roster_card_fields as Team['rosterCardFields'],
    createdAt: r.created_at as string, updatedAt: r.updated_at as string,
  }
}

// ── Seasons ──────────────────────────────────────────────────────────────────

export function seasonToRow(s: Season) {
  return {
    id: s.id, team_id: s.teamId, name: s.name, year: s.year,
    is_active: s.isActive, is_locked: s.isLocked ?? false,
    start_date: s.startDate ?? null, end_date: s.endDate ?? null,
    created_at: s.createdAt, updated_at: s.updatedAt,
  }
}
export function rowToSeason(r: Record<string, unknown>): Season {
  return {
    id: r.id as string, teamId: r.team_id as string, name: r.name as string,
    year: r.year as number, isActive: r.is_active as boolean,
    isLocked: (r.is_locked as boolean | undefined) ?? false,
    startDate: r.start_date as string | undefined, endDate: r.end_date as string | undefined,
    shareToken: r.share_token as string | undefined,
    rosterShareToken: r.roster_share_token as string | undefined,
    createdAt: r.created_at as string, updatedAt: r.updated_at as string,
  }
}

// ── Players ──────────────────────────────────────────────────────────────────

// Note: Player.photoUrl is intentionally omitted — base64 images belong in
// Supabase Storage, not a Postgres column. Photos are device-local only.
export function playerToRow(p: Player, userId: string) {
  return {
    id: p.id, user_id: userId, first_name: p.firstName, last_name: p.lastName,
    number: p.number ?? null, position: p.position ?? null,
    is_active: p.isActive, is_available: p.isAvailable,
    injury_note: p.injuryNote ?? null, notes: p.notes ?? null,
    height: p.height ?? null, weight: p.weight ?? null,
    date_of_birth: p.dateOfBirth ?? null, graduation_year: p.graduationYear ?? null,
    grade: p.grade ?? null, dominant_hand: p.dominantHand ?? null,
    hometown: p.hometown ?? null, high_school: p.highSchool ?? null,
    email: p.email ?? null, phone: p.phone ?? null,
    created_at: p.createdAt, updated_at: p.updatedAt,
  }
}
export function rowToPlayer(r: Record<string, unknown>): Player {
  return {
    id: r.id as string, firstName: r.first_name as string, lastName: r.last_name as string,
    number: r.number as string | undefined, position: r.position as string | undefined,
    isActive: r.is_active as boolean, isAvailable: r.is_available as boolean,
    injuryNote: r.injury_note as string | undefined, notes: r.notes as string | undefined,
    height: r.height as string | undefined, weight: r.weight as string | undefined,
    dateOfBirth: r.date_of_birth as string | undefined,
    graduationYear: r.graduation_year as number | undefined,
    grade: r.grade as string | undefined,
    dominantHand: r.dominant_hand as Player['dominantHand'],
    hometown: r.hometown as string | undefined,
    highSchool: r.high_school as string | undefined,
    email: r.email as string | undefined, phone: r.phone as string | undefined,
    createdAt: r.created_at as string, updatedAt: r.updated_at as string,
  }
}

// ── Season Rosters ────────────────────────────────────────────────────────────

export function rosterToRow(r: SeasonRoster) {
  return { season_id: r.seasonId, player_id: r.playerId, role: r.role, coach_notes: r.coachNotes ?? null }
}
export function rowToRoster(r: Record<string, unknown>): SeasonRoster {
  return {
    seasonId: r.season_id as string, playerId: r.player_id as string,
    role: r.role as SeasonRoster['role'], coachNotes: r.coach_notes as string | undefined,
  }
}

// ── Games ─────────────────────────────────────────────────────────────────────

export function gameToRow(g: Game) {
  return {
    id: g.id, season_id: g.seasonId, opponent: g.opponent, game_date: g.gameDate,
    location: g.location ?? null, result: g.result ?? null,
    home_score: g.homeScore ?? null, opponent_score: g.opponentScore ?? null,
    notes: g.notes ?? null, attendance: g.attendance ?? null,
    created_at: g.createdAt, updated_at: g.updatedAt,
  }
}
export function rowToGame(r: Record<string, unknown>): Game {
  return {
    id: r.id as string, seasonId: r.season_id as string, opponent: r.opponent as string,
    gameDate: r.game_date as string, location: r.location as string | undefined,
    result: r.result as Game['result'], homeScore: r.home_score as number | undefined,
    opponentScore: r.opponent_score as number | undefined, notes: r.notes as string | undefined,
    attendance: r.attendance as Game['attendance'],
    shareToken: r.share_token as string | undefined,
    availabilityToken: r.availability_token as string | undefined,
    createdAt: r.created_at as string, updatedAt: r.updated_at as string,
  }
}

// ── Stat Entries ──────────────────────────────────────────────────────────────

export function statEntryToRow(e: StatEntry) {
  return {
    id: e.id, game_id: e.gameId, player_id: e.playerId, stat_key: e.statKey,
    value: e.value, period: e.period ?? null, minute: e.minute ?? null,
    notes: e.notes ?? null, recorded_at: e.recordedAt,
  }
}
export function rowToStatEntry(r: Record<string, unknown>): StatEntry {
  return {
    id: r.id as string, gameId: r.game_id as string, playerId: r.player_id as string,
    statKey: r.stat_key as string, value: r.value as number,
    period: r.period as string | undefined, minute: r.minute as number | undefined,
    notes: r.notes as string | undefined, recordedAt: r.recorded_at as string,
  }
}

// ── Lineups ───────────────────────────────────────────────────────────────────

export function lineupToRow(l: Lineup) {
  return {
    id: l.id, game_id: l.gameId, name: l.name, period: l.period ?? null,
    batting_order: l.battingOrder ?? null,
    created_at: l.createdAt, updated_at: l.updatedAt,
  }
}
export function rowToLineup(r: Record<string, unknown>): Lineup {
  return {
    id: r.id as string, gameId: r.game_id as string, name: r.name as string,
    period: r.period as string | undefined,
    battingOrder: Array.isArray(r.batting_order) ? (r.batting_order as string[]) : undefined,
    slots: [],
    createdAt: r.created_at as string, updatedAt: r.updated_at as string,
  }
}

// ── Lineup Slots ──────────────────────────────────────────────────────────────

export function slotToRow(s: LineupSlot) {
  return { lineup_id: s.lineupId, player_id: s.playerId, position: s.position ?? null, order: s.order }
}
export function rowToSlot(r: Record<string, unknown>): LineupSlot {
  return {
    lineupId: r.lineup_id as string, playerId: r.player_id as string,
    position: r.position as string | undefined, order: r.order as number,
  }
}

// ── Calendar Events ───────────────────────────────────────────────────────────

export function eventToRow(e: CalendarEvent) {
  return {
    id: e.id, season_id: e.seasonId, type: e.type, title: e.title,
    date: e.date, location: e.location ?? null, notes: e.notes ?? null,
    plan: e.plan ?? null,
    created_at: e.createdAt, updated_at: e.updatedAt,
  }
}
export function rowToEvent(r: Record<string, unknown>): CalendarEvent {
  return {
    id: r.id as string, seasonId: r.season_id as string,
    type: r.type as CalendarEvent['type'], title: r.title as string,
    date: r.date as string, location: r.location as string | undefined,
    notes: r.notes as string | undefined,
    plan: r.plan as CalendarEvent['plan'],
    createdAt: r.created_at as string, updatedAt: r.updated_at as string,
  }
}

// ── Opponents ─────────────────────────────────────────────────────────────────

export function opponentToRow(o: Opponent) {
  return {
    id: o.id, team_id: o.teamId, name: o.name, notes: o.notes ?? null,
    created_at: o.createdAt, updated_at: o.updatedAt,
  }
}
export function rowToOpponent(r: Record<string, unknown>): Opponent {
  return {
    id: r.id as string, teamId: r.team_id as string, name: r.name as string,
    notes: r.notes as string | undefined,
    createdAt: r.created_at as string, updatedAt: r.updated_at as string,
  }
}

// ── Drill Templates ───────────────────────────────────────────────────────────

export function drillTemplateToRow(d: DrillTemplate) {
  return {
    id: d.id, team_id: d.teamId, name: d.name,
    duration_minutes: d.durationMinutes, focus_area: d.focusArea ?? null,
    notes: d.notes ?? null, usage_count: d.usageCount,
    created_at: d.createdAt, updated_at: d.updatedAt,
  }
}
export function rowToDrillTemplate(r: Record<string, unknown>): DrillTemplate {
  return {
    id: r.id as string, teamId: r.team_id as string, name: r.name as string,
    durationMinutes: r.duration_minutes as number, focusArea: r.focus_area as string | undefined,
    notes: r.notes as string | undefined, usageCount: (r.usage_count as number) ?? 0,
    createdAt: r.created_at as string, updatedAt: r.updated_at as string,
  }
}
